import type { ReactNode } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { useAppTheme } from '@/theme/ThemeContext'

type Apply = { disabled: boolean; onApply: () => void }

/**
 * The frame of every column filter. The title and the one clear button are at the top, the
 * body is below them, and a kind that needs an apply step gets the apply button at the bottom.
 */
const FilterCard = ({
  title,
  width = 280,
  isActive,
  onClear,
  apply,
  children,
}: {
  title: string
  width?: number
  isActive: boolean
  onClear: () => void
  apply?: Apply
  children: ReactNode
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const ink = isDarkMode ? '#FFFFFF' : '#003F61'

  return (
    <Box
      sx={{
        width,
        p: 2,
        direction: 'rtl',
        bgcolor: isDarkMode ? '#202B40' : '#FFFFFF',
        borderRadius: '8px',
        border: isDarkMode ? '1px solid #445876' : '1px solid #E4E7EB',
        boxShadow: isDarkMode
          ? '0 8px 24px rgba(0, 0, 0, 0.4)'
          : '0 8px 24px rgba(0, 0, 0, 0.12)',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            color: ink,
            fontSize: '13px',
            fontWeight: 600,
            fontFamily: 'Rubik, sans-serif',
            textAlign: 'right',
          }}
        >
          {title}
        </Typography>
        <Button
          variant="text"
          size="small"
          disabled={!isActive}
          onClick={onClear}
          sx={{
            color: isDarkMode ? '#60A5FA' : '#0B47A8',
            fontFamily: 'Rubik, sans-serif',
            fontSize: '13px',
            fontWeight: 500,
            textTransform: 'none',
            minWidth: 0,
            px: 1,
            py: 0.25,
            '&:hover': { bgcolor: isDarkMode ? '#1D325A' : '#EFF2F7' },
            '&.Mui-disabled': { color: isDarkMode ? '#6B7A90' : '#A0AEC0' },
          }}
        >
          ניקוי
        </Button>
      </Box>

      {children}

      {apply && (
        <Button
          variant="contained"
          size="small"
          fullWidth
          disabled={apply.disabled}
          onClick={apply.onApply}
          sx={{
            mt: 2,
            bgcolor: isDarkMode ? '#2563EB' : '#003F61',
            color: '#FFFFFF',
            fontFamily: 'Rubik, sans-serif',
            fontSize: '13px',
            fontWeight: 600,
            textTransform: 'none',
            height: '36px',
            borderRadius: '6px',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: isDarkMode ? '#1D4ED8' : '#002B44',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              bgcolor: `${isDarkMode ? '#162645' : '#E2E8F0'} !important`,
              color: `${isDarkMode ? '#A0AEC0' : '#94A3B8'} !important`,
              borderColor: `${isDarkMode ? '#445876' : '#CBD5E1'} !important`,
              border: '1px solid',
              opacity: 0.8,
            },
          }}
        >
          סינון
        </Button>
      )}
    </Box>
  )
}

export default FilterCard
